import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { GlobalDataService } from 'src/app/shared/services/global-data.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { SponsorType } from '../sponsor-type.enum';
import { SPONSOR_TYPE_OPTIONS } from '../sponsor-type.helpers';
import { SponsorService } from '../sponsor.service';

@Component({
  selector: 'app-sponsor-edit',
  templateUrl: './sponsor-edit.component.html',
  styleUrls: ['./sponsor-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SponsorEditComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  public canEditSponsor = true;
  public readonly sponsorTypeOptions = SPONSOR_TYPE_OPTIONS;
  public id: string;
  private readonly destroyed$ = new Subject<void>();

  public form = new FormGroup({
    name: new FormControl('', { validators: Validators.required }),
    company: new FormControl(''),
    contactEmail: new FormControl('', {
      validators: [Validators.required, Validators.email]
    }),
    requestReceipt: new FormControl(false),
    amount: new FormControl<number | null>(null),
    hasPaid: new FormControl(false),
    type: new FormControl(SponsorType.Donation),
    notes: new FormControl(''),
    outingIterationId: new FormControl<string | null>(null),
    createdBy: new FormControl<string | null>(null)
  });

  constructor(
    public sessionService: SessionService,
    private globalDataService: GlobalDataService,
    private route: ActivatedRoute,
    private router: Router,
    stringHelpers: StringHelpers,
    private sponsorService: SponsorService,
    private cdRef: ChangeDetectorRef
  ) {
    super(sessionService, stringHelpers);
  }

  ngOnInit(): void {
    if (this.isGlobalOutingSet()) {
      this.selectedIteration = this.getGlobalOutingIteration();
      this.canEditSponsor = this.checkIterationDate();
      this.id = this.route.snapshot.paramMap.get('id');
      if (this.canEditSponsor) {
        this.getSponsor();
      } else {
        this.cdRef.markForCheck();
      }
    }
  }

  ngAfterViewInit(): void {
    this.globalDataService.iterationSelectionEvent.pipe(takeUntil(this.destroyed$)).subscribe((result) => {
      this.selectedIteration = result;
      this.canEditSponsor = this.checkIterationDate();
      this.getSponsor();
      this.cdRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getSponsor(): void {
    if (!this.id) {
      return;
    }
    this.isLoading = true;
    this.cdRef.markForCheck();
    this.sponsorService.getById(this.id).subscribe({
      next: (result) => {
        this.form.patchValue(result);
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  onSubmit({ value, valid }: FormGroup): void {
    if (!valid) {
      return;
    }
    this.isLoading = true;
    this.cdRef.markForCheck();
    value.lastEditedBy = this.sessionService.getUserId();
    value.outingIterationId = this.selectedIteration.id;
    this.sponsorService.update(this.id, value).subscribe({
      next: () => {
        this.isLoading = false;
        this.cdRef.markForCheck();
        this.router.navigate([`/outing-management/administration/sponsor/details/${this.id}`]);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.setError(err, this.form);
        this.cdRef.markForCheck();
      }
    });
  }
}
